import { useState } from 'react'
import { ActivityIndicator, Pressable, Text, TextInput, View } from 'react-native'
import { ArrowRight, EnvelopeSimple, Lock, Phone, User } from 'phosphor-react-native'
import { Theme } from '@monorepo/relayably/theme'

import { submitAuth } from '../../auth/authSlice'
import { useAppDispatch, useAppSelector } from '../../store'
import { createPaneStyles } from './shared'

export const SignUpPane = ({
  theme,
  onOtpRequest,
  onSwitchToLogin,
}: {
  theme: Theme
  onOtpRequest: (phone: string, fullName: string) => void
  onSwitchToLogin: () => void
}) => {
  const styles = createPaneStyles(theme)
  const dispatch = useAppDispatch()
  const auth = useAppSelector(state => state.auth)
  const [method, setMethod] = useState<'email' | 'phone'>('email')
  const [fullName, setFullName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [phone, setPhone] = useState('')
  const isLoading = auth.status === 'loading'

  return (
    <View style={styles.pane}>
      <View style={styles.tabs}>
        <Pressable disabled={isLoading} onPress={() => setMethod('email')} style={[styles.tab, method === 'email' ? styles.tabActive : undefined, isLoading ? styles.buttonDisabled : undefined]}>
          <EnvelopeSimple color={method === 'email' ? theme.colors.text : theme.colors.inactive} size={16} />
          <Text style={[styles.tabText, method === 'email' ? styles.tabTextActive : undefined]}>Email</Text>
        </Pressable>
        <Pressable disabled={isLoading} onPress={() => setMethod('phone')} style={[styles.tab, method === 'phone' ? styles.tabActive : undefined, isLoading ? styles.buttonDisabled : undefined]}>
          <Phone color={method === 'phone' ? theme.colors.text : theme.colors.inactive} size={16} />
          <Text style={[styles.tabText, method === 'phone' ? styles.tabTextActive : undefined]}>Phone</Text>
        </Pressable>
      </View>

      <View style={styles.form}>
        <View style={styles.fieldGroup}>
          <Text style={styles.label}>Full Name</Text>
          <View style={styles.inputIconWrap}>
            <User color={theme.colors.inactive} size={18} style={styles.inputIcon} />
            <TextInput autoCapitalize='words' editable={!isLoading} onChangeText={setFullName} style={[styles.input, styles.iconInput]} value={fullName} />
          </View>
        </View>

        {method === 'email' ? (
          <>
            <View style={styles.fieldGroup}>
              <Text style={styles.label}>Email Address</Text>
              <View style={styles.inputIconWrap}>
                <EnvelopeSimple color={theme.colors.inactive} size={18} style={styles.inputIcon} />
                <TextInput
                  autoCapitalize='none'
                  editable={!isLoading}
                  keyboardType='email-address'
                  onChangeText={setEmail}
                  style={[styles.input, styles.iconInput]}
                  value={email}
                />
              </View>
            </View>
            <View style={styles.fieldGroup}>
              <Text style={styles.label}>Password</Text>
              <View style={styles.inputIconWrap}>
                <Lock color={theme.colors.inactive} size={18} style={styles.inputIcon} />
                <TextInput editable={!isLoading} onChangeText={setPassword} secureTextEntry style={[styles.input, styles.iconInput]} value={password} />
              </View>
            </View>
          </>
        ) : (
          <View style={styles.fieldGroup}>
            <Text style={styles.label}>Phone Number</Text>
            <View style={styles.row}>
              <View style={[styles.input, { minWidth: 78, paddingHorizontal: 12 }]}>
                <Text style={{ color: theme.colors.text, fontSize: 14, textAlign: 'center' }}>+61</Text>
              </View>
              <TextInput editable={!isLoading} keyboardType='phone-pad' onChangeText={setPhone} style={[styles.input, { flex: 1 }]} value={phone} />
            </View>
          </View>
        )}

        {auth.errorMessage ? <Text style={styles.errorText}>{auth.errorMessage}</Text> : null}

        <Pressable
          disabled={isLoading}
          onPress={() => {
            if (method === 'phone') {
              onOtpRequest(phone, fullName)
              return
            }

            void dispatch(
              submitAuth({
                mode: 'sign-up',
                method: 'email',
                email,
                password,
                fullName,
                phoneNumber: '',
                otp: '',
              })
            )
          }}
          style={[styles.submitButton, isLoading ? styles.submitButtonDisabled : undefined]}
        >
          {isLoading ? (
            <ActivityIndicator color={theme.colors.staticWhite} size='small' />
          ) : (
            <>
              <Text style={styles.submitButtonText}>{method === 'phone' ? 'Verify Phone' : 'Create Account'}</Text>
              <ArrowRight color={theme.colors.staticWhite} size={16} weight='bold' />
            </>
          )}
        </Pressable>
      </View>

      <Text style={styles.legalText}>
        By continuing, you agree to our <Text style={{ color: theme.colors.text, textDecorationLine: 'underline' }}>Terms of Service</Text> and{' '}
        <Text style={{ color: theme.colors.text, textDecorationLine: 'underline' }}>Privacy Policy</Text>.
      </Text>

      <Text style={styles.footerText}>
        Already have an account?
        <Text onPress={isLoading ? undefined : onSwitchToLogin} style={styles.actionLink}>
          {' '}Sign In
        </Text>
      </Text>
    </View>
  )
}
